"use client";
import React, { createContext, useContext, useState } from "react";
import ModalTemplate from "../components/ModalTemplate";

export type ModalConfig = {
    hideCloseButton?: boolean;
    contentWrapperClass?: string;
    closeButtonClass?: string;
};
interface ModalData {
    id: string;
    content: React.ReactNode;
    title?: string;
    onClose?: () => void;
    config?: ModalConfig;
}

interface ModalContextType {
    showModal: (id: string, content: React.ReactNode, title?: string, onClose?: () => void, config?: ModalData["config"]) => void;
    closeModal: (id: string) => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

interface ModalProviderProps {
    children: React.ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
    const [modals, setModals] = useState<ModalData[]>([]);

    const showModal = (id: string, content: React.ReactNode, title?: string, onClose?: () => void, config?: ModalData["config"]): void => {
        // Config defaults:
        const finalConfig = {
            hideCloseButton: false,
            ...config
        };

        setModals(prevModals => [...prevModals, { id, content, title, onClose, config: finalConfig }]);
    };

    const closeModal = (id: string): void => {
        setModals(prevModals => prevModals.filter(modal => modal.id !== id));
    };

    return (
        <ModalContext.Provider value={{ showModal, closeModal }}>
            {children}
            {modals.map(modal => (
                <ModalTemplate
                    key={modal.id}
                    onClose={() => {
                        modal.onClose?.(); // calls optional onClose callback
                        closeModal(modal.id); // closes the modal
                    }}
                    title={modal.title}
                    config={modal.config}
                >
                    {modal.content}
                </ModalTemplate>
            ))}
        </ModalContext.Provider>
    );
};

export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error("useModal must be used within a ModalProvider");
    }
    return context;
};
